import { type ReactNode, useState } from 'react';
import { useFieldArray, useForm } from 'react-hook-form';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { ColumnDef } from '@tanstack/react-table';
import { AlertTriangle, ArrowLeftRight, Boxes, CheckCircle2, ClipboardCheck, Download, FileText, LayoutDashboard, PackagePlus, Plus, Printer, Save, Trash2, Warehouse } from 'lucide-react';
import { Button, DataTable, FormField, Modal, PageHeader, StatusBadge, Tabs } from '@/components/ui';
import { Input } from '@/components/ui/input';
import { getApiErrorMessage } from '@/lib/axios';
import {
  type ArticleDTO,
  type BonSortieDTO,
  type InventaireDTO,
  type TransfertDTO,
  ARTICLE_CATEGORIES,
  CATEGORIE_LABEL,
  UNITES_STOCK,
  createArticle,
  createBonSortie,
  createDepot,
  createInventaire,
  createTransfert,
  deleteBonSortie,
  deleteInventaire,
  getDashboardStock,
  getReapprovisionnement,
  listArticles,
  listBonsSortie,
  listDepots,
  listInventaires,
  listTransferts,
  openBonSortiePdf,
  saisirInventaire,
  validerBonSortie,
  validerInventaire,
} from '@/services/stock';
import { createDA } from '@/services/achats';
import { usePerms } from '@/hooks/usePerms';
import { listChantiers } from '@/services/chantiers';
import { toast } from '@/store/toastStore';
import type { StatusKey } from '@/types';
import { formatDZD } from '@/utils/algeria';

const selectCls = 'flex h-10 w-full rounded-md border border-border bg-surface-secondary px-3 text-sm';
const BS_UI: Record<string, { label: string; status: StatusKey }> = {
  BROUILLON: { label: 'Brouillon', status: 'pending' },
  VALIDE: { label: 'Validé', status: 'done' },
  ANNULE: { label: 'Annulé', status: 'stuck' },
};

export function StockPage() {
  const [tab, setTab] = useState('dashboard');
  return (
    <>
      <PageHeader
        title="Stock & approvisionnements"
        subtitle="Articles, mouvements (grand-livre CMP) et imputation des matériaux aux chantiers"
        breadcrumbs={[{ label: 'Advanced Works ERP' }, { label: 'Stock' }]}
      />
      <Tabs
        tabs={[
          { key: 'dashboard', label: 'Tableau de bord', icon: <LayoutDashboard className="size-4" /> },
          { key: 'articles', label: 'Articles', icon: <Boxes className="size-4" /> },
          { key: 'bs', label: 'Bons de sortie', icon: <FileText className="size-4" /> },
          { key: 'transferts', label: 'Transferts', icon: <ArrowLeftRight className="size-4" /> },
          { key: 'inventaire', label: 'Inventaire', icon: <ClipboardCheck className="size-4" /> },
          { key: 'depots', label: 'Dépôts', icon: <Warehouse className="size-4" /> },
        ]}
        value={tab}
        onChange={setTab}
        className="mb-6"
      />
      {tab === 'dashboard' && <DashboardTab />}
      {tab === 'articles' && <ArticlesTab />}
      {tab === 'bs' && <BonsSortieTab />}
      {tab === 'transferts' && <TransfertsTab />}
      {tab === 'inventaire' && <InventaireTab />}
      {tab === 'depots' && <DepotsTab />}
    </>
  );
}

function StatCard({ label, value, icon, accent }: { label: string; value: string; icon: ReactNode; accent?: 'warning' }) {
  return (
    <div className="rounded-lg border border-border bg-surface-secondary p-4 shadow-sm">
      <div className="flex items-center justify-between">
        <p className="label-caps text-content-muted">{label}</p>
        <span className={accent === 'warning' ? 'text-status-stuck' : 'text-brand-primary'}>{icon}</span>
      </div>
      <p className={`mt-2 text-2xl font-bold tabular-nums ${accent === 'warning' ? 'text-status-stuck' : 'text-content-primary'}`}>{value}</p>
    </div>
  );
}

function DashboardTab() {
  const { data, isLoading } = useQuery({ queryKey: ['stock-dashboard'], queryFn: getDashboardStock });
  if (isLoading || !data) return <DataTable columns={[]} data={[]} loading />;
  const maxSortie = Math.max(1, ...data.top_sorties.map((t) => Number(t.valeur)));
  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <StatCard label="Valeur totale du stock" value={formatDZD(data.valeur_totale)} icon={<Boxes className="size-5" />} />
        <StatCard label="Articles gérés" value={String(data.nb_articles)} icon={<FileText className="size-5" />} />
        <StatCard label="Sous le seuil" value={String(data.nb_sous_seuil)} icon={<AlertTriangle className="size-5" />} accent={data.nb_sous_seuil > 0 ? 'warning' : undefined} />
        <StatCard label="Dépôts actifs" value={String(data.nb_depots)} icon={<Warehouse className="size-5" />} />
      </div>

      <div className="rounded-lg border border-border bg-surface-secondary p-5 shadow-sm">
        <h3 className="mb-4 text-sm font-semibold text-content-primary">Top 5 chantiers — matériaux consommés (valorisés au CMP)</h3>
        {data.top_sorties.length === 0 ? (
          <p className="text-sm text-content-muted">Aucune sortie de stock enregistrée.</p>
        ) : (
          <div className="space-y-3">
            {data.top_sorties.map((t) => (
              <div key={t.chantier}>
                <div className="mb-1 flex items-center justify-between text-sm">
                  <span className="text-content-secondary">{t.chantier}</span>
                  <span className="font-medium tabular-nums text-content-primary">{formatDZD(t.valeur)}</span>
                </div>
                <div className="h-2 w-full overflow-hidden rounded-full bg-surface-hover">
                  <div className="h-full rounded-full bg-brand-primary" style={{ width: `${(Number(t.valeur) / maxSortie) * 100}%` }} />
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      <ReapproPanel />
    </div>
  );
}

function ReapproPanel() {
  const queryClient = useQueryClient();
  const { data, isLoading } = useQuery({ queryKey: ['reappro'], queryFn: getReapprovisionnement });
  const lignes = data ?? [];

  const daMutation = useMutation({
    mutationFn: () =>
      createDA({
        notes: 'Réapprovisionnement (articles sous le seuil)',
        lignes: lignes.map((l) => ({ designation: l.designation, unite: l.unite, quantite: Number(l.quantite_suggeree), prix_unitaire: Number(l.cmp_courant), article_stock_id: l.article_id })),
      }),
    onSuccess: () => { queryClient.invalidateQueries({ queryKey: ['demandes-achat'] }); toast.success("Demande d'achat créée (brouillon) dans le module Achats."); },
    onError: (e) => toast.error(getApiErrorMessage(e)),
  });

  return (
    <div className="rounded-lg border border-border bg-surface-secondary p-5 shadow-sm">
      <div className="mb-4 flex items-center justify-between">
        <h3 className="flex items-center gap-2 text-sm font-semibold text-content-primary"><AlertTriangle className="size-4 text-status-stuck" /> À réapprovisionner (sous le seuil)</h3>
        {lignes.length > 0 && <Button size="sm" onClick={() => daMutation.mutate()} loading={daMutation.isPending}><PackagePlus className="size-4" /> Créer une demande d'appro</Button>}
      </div>
      {isLoading ? (
        <p className="text-sm text-content-muted">Chargement…</p>
      ) : lignes.length === 0 ? (
        <p className="text-sm text-content-muted">Aucun article sous le seuil. ✔</p>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-border text-left text-xs text-content-muted">
                <th className="py-2 pr-3">Article</th>
                <th className="py-2 pr-3 text-right">Stock</th>
                <th className="py-2 pr-3 text-right">Seuil</th>
                <th className="py-2 pr-3 text-right">Qté suggérée</th>
              </tr>
            </thead>
            <tbody>
              {lignes.map((l) => (
                <tr key={l.article_id} className="border-b border-border/50">
                  <td className="py-2 pr-3">{l.reference ? <span className="text-content-muted">{l.reference} · </span> : null}{l.designation}</td>
                  <td className="py-2 pr-3 text-right font-semibold tabular-nums text-status-stuck">{Number(l.stock_total)} {l.unite ?? ''}</td>
                  <td className="py-2 pr-3 text-right tabular-nums text-content-secondary">{Number(l.seuil_min)}</td>
                  <td className="py-2 pr-3 text-right font-medium tabular-nums text-brand-primary">{Number(l.quantite_suggeree)} {l.unite ?? ''}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function ArticlesTab() {
  const queryClient = useQueryClient();
  const perms = usePerms('stock');
  const [open, setOpen] = useState(false);
  const { data, isLoading } = useQuery({ queryKey: ['articles'], queryFn: () => listArticles({ size: 500 }) });
  const { register, handleSubmit, reset } = useForm<{ designation: string; categorie: ArticleDTO['categorie']; unite: string; seuil_min: number; stockable: boolean }>({
    defaultValues: { categorie: 'MATERIAUX', unite: 'u', seuil_min: 0, stockable: true },
  });

  const createMutation = useMutation({
    mutationFn: (v: { designation: string; categorie: ArticleDTO['categorie']; unite: string; seuil_min: number; stockable: boolean }) =>
      createArticle({ designation: v.designation, categorie: v.categorie, unite: v.unite || null, seuil_min: Number(v.seuil_min) || 0, stockable: !!v.stockable }),
    onSuccess: () => { queryClient.invalidateQueries({ queryKey: ['articles'] }); setOpen(false); reset({ categorie: 'MATERIAUX', unite: 'u', seuil_min: 0, stockable: true }); toast.success('Article créé.'); },
    onError: (e) => toast.error(getApiErrorMessage(e)),
  });

  const columns: ColumnDef<ArticleDTO, unknown>[] = [
    { accessorKey: 'reference', header: 'Réf.', size: 90 },
    { accessorKey: 'designation', header: 'Désignation' },
    { accessorKey: 'categorie', header: 'Catégorie', size: 120, cell: ({ getValue }) => CATEGORIE_LABEL[getValue() as ArticleDTO['categorie']] },
    { accessorKey: 'unite', header: 'Unité', size: 70, cell: ({ getValue }) => (getValue() as string) ?? '—' },
    { accessorKey: 'stock_total', header: 'Stock', size: 100, cell: ({ row }) => <span className={row.original.sous_seuil ? 'font-semibold text-status-stuck' : 'tabular-nums'}>{Number(row.original.stock_total)}{row.original.sous_seuil ? ' ⚠' : ''}</span> },
    { accessorKey: 'cmp_courant', header: 'CMP', size: 110, cell: ({ getValue }) => <span className="tabular-nums">{formatDZD(getValue() as number)}</span> },
    { accessorKey: 'valeur_stock', header: 'Valeur stock', cell: ({ getValue }) => <span className="tabular-nums font-medium">{formatDZD(getValue() as number)}</span> },
  ];

  const valeurTotale = (data?.items ?? []).reduce((s, a) => s + Number(a.valeur_stock), 0);

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <p className="text-sm text-content-secondary">Valeur totale du stock : <span className="font-bold tabular-nums text-brand-primary">{formatDZD(valeurTotale)}</span></p>
        {perms.canAjouter && <Button onClick={() => setOpen(true)}><Plus /> Nouvel article</Button>}
      </div>
      <DataTable columns={columns} data={data?.items ?? []} loading={isLoading} pageSize={20} exportFileName="articles" emptyMessage="Aucun article." />

      <Modal open={open} onOpenChange={setOpen} title="Nouvel article" footer={<><Button variant="secondary" onClick={() => setOpen(false)}>Annuler</Button><Button onClick={handleSubmit((v) => createMutation.mutate(v))} loading={createMutation.isPending}>Créer</Button></>}>
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <FormField label="Désignation" required className="sm:col-span-2"><Input {...register('designation')} /></FormField>
          <FormField label="Catégorie"><select {...register('categorie')} className={selectCls}>{ARTICLE_CATEGORIES.map((c) => <option key={c} value={c}>{CATEGORIE_LABEL[c]}</option>)}</select></FormField>
          <FormField label="Unité"><select {...register('unite')} className={selectCls}>{UNITES_STOCK.map((u) => <option key={u} value={u}>{u}</option>)}</select></FormField>
          <FormField label="Seuil minimum (alerte)"><Input type="number" step="0.001" {...register('seuil_min')} /></FormField>
          <label className="flex items-center gap-2 self-end pb-2 text-sm text-content-secondary"><input type="checkbox" className="size-4 accent-brand-primary" {...register('stockable')} /> Stockable (passe par un dépôt)</label>
        </div>
      </Modal>
    </div>
  );
}

function BonsSortieTab() {
  const queryClient = useQueryClient();
  const perms = usePerms('stock');
  const [open, setOpen] = useState(false);
  const { data, isLoading } = useQuery({ queryKey: ['bons-sortie'], queryFn: listBonsSortie });
  const { data: depots } = useQuery({ queryKey: ['depots'], queryFn: listDepots, enabled: open });
  const { data: chantiers } = useQuery({ queryKey: ['chantiers'], queryFn: () => listChantiers(), enabled: open });
  const { data: articles } = useQuery({ queryKey: ['articles'], queryFn: () => listArticles({ size: 500 }), enabled: open });

  const { register, handleSubmit, control, reset } = useForm<{ depot_id: string; chantier_id: string; notes: string; lignes: { article_id: string; quantite: number }[] }>({
    defaultValues: { lignes: [{ article_id: '', quantite: 1 }] },
  });
  const { fields, append, remove } = useFieldArray({ control, name: 'lignes' });
  const invalidate = () => queryClient.invalidateQueries({ queryKey: ['bons-sortie'] });

  const createMutation = useMutation({
    mutationFn: (v: { depot_id: string; chantier_id: string; notes: string; lignes: { article_id: string; quantite: number }[] }) =>
      createBonSortie({ depot_id: v.depot_id, chantier_id: v.chantier_id, notes: v.notes || null, lignes: v.lignes.filter((l) => l.article_id).map((l) => ({ article_id: l.article_id, quantite: Number(l.quantite) || 0 })) }),
    onSuccess: () => { invalidate(); setOpen(false); reset({ lignes: [{ article_id: '', quantite: 1 }] }); toast.success('Bon de sortie créé (brouillon).'); },
    onError: (e) => toast.error(getApiErrorMessage(e)),
  });
  const validerMutation = useMutation({ mutationFn: validerBonSortie, onSuccess: (bs) => { invalidate(); queryClient.invalidateQueries({ queryKey: ['articles'] }); toast.success(`Bon de sortie validé : ${bs.reference}.`); }, onError: (e) => toast.error(getApiErrorMessage(e)) });
  const deleteMutation = useMutation({ mutationFn: deleteBonSortie, onSuccess: () => { invalidate(); toast.success('Bon de sortie supprimé.'); }, onError: (e) => toast.error(getApiErrorMessage(e)) });

  const columns: ColumnDef<BonSortieDTO, unknown>[] = [
    { accessorKey: 'reference', header: 'Référence', size: 140, cell: ({ getValue }) => (getValue() as string) ?? <span className="text-content-muted italic">Brouillon</span> },
    { accessorKey: 'date_sortie', header: 'Date', size: 100, cell: ({ getValue }) => (getValue() as string) ?? '—' },
    { id: 'lignes', header: 'Articles', size: 80, cell: ({ row }) => `${row.original.lignes.length}` },
    { accessorKey: 'valeur_totale', header: 'Valeur (CMP)', cell: ({ getValue }) => <span className="tabular-nums font-medium">{formatDZD(getValue() as number)}</span> },
    { accessorKey: 'statut', header: 'Statut', size: 100, cell: ({ getValue }) => { const ui = BS_UI[getValue() as string]; return <StatusBadge status={ui.status} label={ui.label} />; } },
    {
      id: 'actions',
      header: '',
      size: 170,
      cell: ({ row }) => {
        const bs = row.original;
        return (
          <div className="flex justify-end gap-1">
            {bs.statut === 'VALIDE' && <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => openBonSortiePdf(bs.id, 'open')} aria-label="Imprimer"><Printer className="size-4" /></Button>}
            {bs.statut === 'VALIDE' && <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => openBonSortiePdf(bs.id, 'download')} aria-label="Télécharger"><Download className="size-4" /></Button>}
            {bs.statut === 'BROUILLON' && <Button variant="ghost" size="icon" className="h-8 w-8 text-status-done" onClick={() => validerMutation.mutate(bs.id)} aria-label="Valider"><CheckCircle2 className="size-4" /></Button>}
            {bs.statut === 'BROUILLON' && <Button variant="ghost" size="icon" className="h-8 w-8 text-status-stuck" onClick={() => { if (window.confirm('Supprimer ce bon de sortie ?')) deleteMutation.mutate(bs.id); }} aria-label="Supprimer"><Trash2 className="size-4" /></Button>}
          </div>
        );
      },
    },
  ];

  return (
    <div className="space-y-4">
      <div className="flex justify-end">{perms.canAjouter && <Button onClick={() => { reset({ lignes: [{ article_id: '', quantite: 1 }] }); setOpen(true); }}><Plus /> Nouveau bon de sortie</Button>}</div>
      <DataTable columns={columns} data={data?.items ?? []} loading={isLoading} pageSize={15} exportFileName="bons-sortie" emptyMessage="Aucun bon de sortie." />

      <Modal open={open} onOpenChange={setOpen} title="Nouveau bon de sortie (vers chantier)" size="lg" footer={<><Button variant="secondary" onClick={() => setOpen(false)}>Annuler</Button><Button onClick={handleSubmit((v) => createMutation.mutate(v))} loading={createMutation.isPending}>Créer (brouillon)</Button></>}>
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <FormField label="Dépôt (source)" required>
            <select {...register('depot_id')} className={selectCls}><option value="">— Sélectionner —</option>{depots?.map((d) => <option key={d.id} value={d.id}>{d.code} · {d.libelle}</option>)}</select>
          </FormField>
          <FormField label="Chantier (imputation)" required>
            <select {...register('chantier_id')} className={selectCls}><option value="">— Sélectionner —</option>{chantiers?.items.map((c) => <option key={c.id} value={c.id}>{c.code} · {c.nom}</option>)}</select>
          </FormField>
        </div>
        <div className="mt-4">
          <div className="mb-2 flex items-center justify-between">
            <p className="label-caps">Articles sortis</p>
            <Button variant="ghost" size="sm" onClick={() => append({ article_id: '', quantite: 1 })}><Plus className="size-4" /> Ligne</Button>
          </div>
          <div className="space-y-2">
            {fields.map((f, i) => (
              <div key={f.id} className="grid grid-cols-12 gap-2">
                <select className={`${selectCls} col-span-8`} {...register(`lignes.${i}.article_id`)}>
                  <option value="">— Article —</option>
                  {articles?.items.filter((a) => a.stockable).map((a) => <option key={a.id} value={a.id}>{a.designation} (stock {Number(a.stock_total)} {a.unite})</option>)}
                </select>
                <Input type="number" step="0.001" placeholder="Qté" className="col-span-3" {...register(`lignes.${i}.quantite`)} />
                <Button variant="ghost" size="icon" className="col-span-1 h-10 text-status-stuck" onClick={() => remove(i)} disabled={fields.length === 1} aria-label="Retirer"><Trash2 className="size-4" /></Button>
              </div>
            ))}
          </div>
        </div>
        <FormField label="Notes" className="mt-3"><Input {...register('notes')} /></FormField>
      </Modal>
    </div>
  );
}

function TransfertsTab() {
  const queryClient = useQueryClient();
  const [open, setOpen] = useState(false);
  const { data, isLoading } = useQuery({ queryKey: ['transferts'], queryFn: listTransferts });
  const { data: depots } = useQuery({ queryKey: ['depots'], queryFn: listDepots, enabled: open });
  const { data: articles } = useQuery({ queryKey: ['articles'], queryFn: () => listArticles({ size: 500 }), enabled: open });

  const { register, handleSubmit, reset } = useForm<{ article_id: string; depot_source_id: string; depot_cible_id: string; quantite: number; motif: string }>();
  const createMutation = useMutation({
    mutationFn: (v: { article_id: string; depot_source_id: string; depot_cible_id: string; quantite: number; motif: string }) =>
      createTransfert({ article_id: v.article_id, depot_source_id: v.depot_source_id, depot_cible_id: v.depot_cible_id, quantite: Number(v.quantite) || 0, motif: v.motif || null }),
    onSuccess: (t) => { queryClient.invalidateQueries({ queryKey: ['transferts'] }); queryClient.invalidateQueries({ queryKey: ['articles'] }); setOpen(false); reset(); toast.success(`Transfert enregistré (${Number(t.quantite)} ${t.article_designation}).`); },
    onError: (e) => toast.error(getApiErrorMessage(e)),
  });

  const columns: ColumnDef<TransfertDTO, unknown>[] = [
    { accessorKey: 'reference', header: 'Référence', size: 130, cell: ({ getValue }) => (getValue() as string) ?? '—' },
    { accessorKey: 'date_mouvement', header: 'Date', size: 100, cell: ({ getValue }) => (getValue() as string) ?? '—' },
    { accessorKey: 'article_designation', header: 'Article' },
    { id: 'trajet', header: 'Trajet', cell: ({ row }) => <span className="text-content-secondary">{row.original.depot_source} <ArrowLeftRight className="inline size-3" /> {row.original.depot_cible}</span> },
    { accessorKey: 'quantite', header: 'Qté', size: 80, cell: ({ getValue }) => <span className="tabular-nums">{Number(getValue() as number)}</span> },
    { accessorKey: 'valeur', header: 'Valeur (CMP)', size: 130, cell: ({ getValue }) => <span className="tabular-nums font-medium">{formatDZD(getValue() as number)}</span> },
  ];

  return (
    <div className="space-y-4">
      <div className="flex justify-end"><Button onClick={() => { reset(); setOpen(true); }}><Plus /> Nouveau transfert</Button></div>
      <DataTable columns={columns} data={data ?? []} loading={isLoading} pageSize={15} exportFileName="transferts" emptyMessage="Aucun transfert." />

      <Modal open={open} onOpenChange={setOpen} title="Transfert inter-dépôts" footer={<><Button variant="secondary" onClick={() => setOpen(false)}>Annuler</Button><Button onClick={handleSubmit((v) => createMutation.mutate(v))} loading={createMutation.isPending}>Transférer</Button></>}>
        <div className="grid grid-cols-1 gap-4">
          <FormField label="Article" required>
            <select {...register('article_id')} className={selectCls}><option value="">— Sélectionner —</option>{articles?.items.filter((a) => a.stockable).map((a) => <option key={a.id} value={a.id}>{a.designation} (stock {Number(a.stock_total)} {a.unite})</option>)}</select>
          </FormField>
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <FormField label="Dépôt source" required>
              <select {...register('depot_source_id')} className={selectCls}><option value="">— Source —</option>{depots?.map((d) => <option key={d.id} value={d.id}>{d.code} · {d.libelle}</option>)}</select>
            </FormField>
            <FormField label="Dépôt cible" required>
              <select {...register('depot_cible_id')} className={selectCls}><option value="">— Cible —</option>{depots?.map((d) => <option key={d.id} value={d.id}>{d.code} · {d.libelle}</option>)}</select>
            </FormField>
          </div>
          <FormField label="Quantité" required><Input type="number" step="0.001" {...register('quantite')} /></FormField>
          <FormField label="Motif"><Input {...register('motif')} placeholder="Ex. approvisionnement chantier" /></FormField>
        </div>
      </Modal>
    </div>
  );
}

function InventaireTab() {
  const queryClient = useQueryClient();
  const [createOpen, setCreateOpen] = useState(false);
  const [detail, setDetail] = useState<InventaireDTO | null>(null);
  const { data, isLoading } = useQuery({ queryKey: ['inventaires'], queryFn: listInventaires });
  const { data: depots } = useQuery({ queryKey: ['depots'], queryFn: listDepots });
  const { data: articles } = useQuery({ queryKey: ['articles'], queryFn: () => listArticles({ size: 500 }) });
  const articleMap = new Map((articles?.items ?? []).map((a) => [a.id, a]));
  const depotMap = new Map((depots ?? []).map((d) => [d.id, `${d.code} · ${d.libelle}`]));
  const invalidate = () => queryClient.invalidateQueries({ queryKey: ['inventaires'] });

  const { register, handleSubmit, reset } = useForm<{ depot_id: string; notes: string }>();
  const createMutation = useMutation({
    mutationFn: (v: { depot_id: string; notes: string }) => createInventaire({ depot_id: v.depot_id, notes: v.notes || null }),
    onSuccess: (inv) => { invalidate(); setCreateOpen(false); reset(); setDetail(inv); toast.success('Inventaire ouvert : saisissez les quantités comptées.'); },
    onError: (e) => toast.error(getApiErrorMessage(e)),
  });
  const deleteMutation = useMutation({ mutationFn: deleteInventaire, onSuccess: () => { invalidate(); toast.success('Inventaire supprimé.'); }, onError: (e) => toast.error(getApiErrorMessage(e)) });

  const columns: ColumnDef<InventaireDTO, unknown>[] = [
    { accessorKey: 'reference', header: 'Référence', size: 130, cell: ({ getValue }) => (getValue() as string) ?? <span className="text-content-muted italic">Brouillon</span> },
    { accessorKey: 'depot_id', header: 'Dépôt', cell: ({ getValue }) => depotMap.get(getValue() as string) ?? '—' },
    { accessorKey: 'date_inventaire', header: 'Date', size: 100, cell: ({ getValue }) => (getValue() as string) ?? '—' },
    { accessorKey: 'nb_ecarts', header: 'Écarts', size: 80, cell: ({ getValue }) => <span className="tabular-nums">{getValue() as number}</span> },
    { accessorKey: 'valeur_ecart_total', header: 'Valeur écarts', size: 130, cell: ({ getValue }) => { const v = Number(getValue() as number); return <span className={`tabular-nums font-medium ${v < 0 ? 'text-status-stuck' : v > 0 ? 'text-status-done' : ''}`}>{formatDZD(v)}</span>; } },
    { accessorKey: 'statut', header: 'Statut', size: 100, cell: ({ getValue }) => { const ui = BS_UI[getValue() as string]; return <StatusBadge status={ui.status} label={ui.label} />; } },
    {
      id: 'actions', header: '', size: 110,
      cell: ({ row }) => {
        const inv = row.original;
        return (
          <div className="flex justify-end gap-1">
            <Button variant="ghost" size="sm" onClick={() => setDetail(inv)}>{inv.statut === 'BROUILLON' ? 'Saisir' : 'Voir'}</Button>
            {inv.statut === 'BROUILLON' && <Button variant="ghost" size="icon" className="h-8 w-8 text-status-stuck" onClick={() => { if (window.confirm('Supprimer cet inventaire ?')) deleteMutation.mutate(inv.id); }} aria-label="Supprimer"><Trash2 className="size-4" /></Button>}
          </div>
        );
      },
    },
  ];

  return (
    <div className="space-y-4">
      <div className="flex justify-end"><Button onClick={() => { reset(); setCreateOpen(true); }}><Plus /> Nouvel inventaire</Button></div>
      <DataTable columns={columns} data={data?.items ?? []} loading={isLoading} pageSize={15} exportFileName="inventaires" emptyMessage="Aucun inventaire." />

      <Modal open={createOpen} onOpenChange={setCreateOpen} title="Nouvel inventaire" footer={<><Button variant="secondary" onClick={() => setCreateOpen(false)}>Annuler</Button><Button onClick={handleSubmit((v) => createMutation.mutate(v))} loading={createMutation.isPending}>Ouvrir l'inventaire</Button></>}>
        <p className="mb-3 text-sm text-content-muted">Le stock théorique de chaque article du dépôt sera figé ; vous saisirez ensuite les quantités physiquement comptées.</p>
        <div className="grid grid-cols-1 gap-4">
          <FormField label="Dépôt" required><select {...register('depot_id')} className={selectCls}><option value="">— Sélectionner —</option>{depots?.map((d) => <option key={d.id} value={d.id}>{d.code} · {d.libelle}</option>)}</select></FormField>
          <FormField label="Notes"><Input {...register('notes')} /></FormField>
        </div>
      </Modal>

      {detail && <InventaireDetailModal key={detail.id} inv={detail} articleMap={articleMap} depotLabel={depotMap.get(detail.depot_id) ?? '—'} onClose={() => setDetail(null)} onChanged={invalidate} />}
    </div>
  );
}

function InventaireDetailModal({ inv, articleMap, depotLabel, onClose, onChanged }: { inv: InventaireDTO; articleMap: Map<string, ArticleDTO>; depotLabel: string; onClose: () => void; onChanged: () => void }) {
  const queryClient = useQueryClient();
  const editable = inv.statut === 'BROUILLON';
  const [phys, setPhys] = useState<Record<string, string>>(() => Object.fromEntries(inv.lignes.map((l) => [l.article_id, String(Number(l.quantite_physique))])));
  const lignesPayload = () => inv.lignes.map((l) => ({ article_id: l.article_id, quantite_physique: Number(phys[l.article_id] ?? l.quantite_physique) || 0 }));
  const refreshAll = () => { onChanged(); queryClient.invalidateQueries({ queryKey: ['articles'] }); };

  const saveMutation = useMutation({
    mutationFn: () => saisirInventaire(inv.id, lignesPayload()),
    onSuccess: () => { onChanged(); toast.success('Saisie enregistrée.'); },
    onError: (e) => toast.error(getApiErrorMessage(e)),
  });
  const validateMutation = useMutation({
    mutationFn: async () => { await saisirInventaire(inv.id, lignesPayload()); return validerInventaire(inv.id); },
    onSuccess: (v) => { refreshAll(); toast.success(`Inventaire validé : ${v.reference} (régularisations émises).`); onClose(); },
    onError: (e) => toast.error(getApiErrorMessage(e)),
  });

  return (
    <Modal open onOpenChange={(o) => { if (!o) onClose(); }} title={`Inventaire ${inv.reference ?? '(brouillon)'} — ${depotLabel}`} size="lg"
      footer={editable ? (
        <><Button variant="secondary" onClick={onClose}>Fermer</Button>
        <Button variant="secondary" onClick={() => saveMutation.mutate()} loading={saveMutation.isPending}><Save className="size-4" /> Enregistrer</Button>
        <Button onClick={() => { if (window.confirm('Valider l\'inventaire ? Les écarts seront régularisés (mouvements de stock).')) validateMutation.mutate(); }} loading={validateMutation.isPending}><CheckCircle2 className="size-4" /> Valider</Button></>
      ) : <Button onClick={onClose}>Fermer</Button>}>
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-border text-left text-xs text-content-muted">
              <th className="py-2 pr-3">Article</th>
              <th className="py-2 pr-3 text-right">Théorique</th>
              <th className="py-2 pr-3 text-right">Compté</th>
              <th className="py-2 pr-3 text-right">Écart</th>
            </tr>
          </thead>
          <tbody>
            {inv.lignes.map((l) => {
              const a = articleMap.get(l.article_id);
              const compte = editable ? Number(phys[l.article_id] ?? 0) : Number(l.quantite_physique);
              const ecart = compte - Number(l.quantite_theorique);
              return (
                <tr key={l.id} className="border-b border-border/50">
                  <td className="py-1.5 pr-3">{a?.designation ?? l.article_id}{a?.unite ? <span className="text-content-muted"> ({a.unite})</span> : null}</td>
                  <td className="py-1.5 pr-3 text-right tabular-nums text-content-secondary">{Number(l.quantite_theorique)}</td>
                  <td className="py-1.5 pr-3 text-right">
                    {editable
                      ? <Input type="number" step="0.001" value={phys[l.article_id] ?? ''} onChange={(e) => setPhys((p) => ({ ...p, [l.article_id]: e.target.value }))} className="h-8 w-24 text-right" />
                      : <span className="tabular-nums">{Number(l.quantite_physique)}</span>}
                  </td>
                  <td className={`py-1.5 pr-3 text-right font-medium tabular-nums ${ecart < 0 ? 'text-status-stuck' : ecart > 0 ? 'text-status-done' : 'text-content-muted'}`}>{ecart > 0 ? '+' : ''}{Number(ecart.toFixed(3))}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </Modal>
  );
}

function DepotsTab() {
  const queryClient = useQueryClient();
  const [open, setOpen] = useState(false);
  const { data, isLoading } = useQuery({ queryKey: ['depots'], queryFn: listDepots });
  const { data: chantiers } = useQuery({ queryKey: ['chantiers'], queryFn: () => listChantiers(), enabled: open });
  const { register, handleSubmit, watch, reset } = useForm<{ libelle: string; type: 'CENTRAL' | 'CHANTIER'; chantier_id: string; adresse: string }>({ defaultValues: { type: 'CENTRAL' } });
  const type = watch('type');

  const createMutation = useMutation({
    mutationFn: (v: { libelle: string; type: 'CENTRAL' | 'CHANTIER'; chantier_id: string; adresse: string }) =>
      createDepot({ libelle: v.libelle, type: v.type, chantier_id: v.type === 'CHANTIER' ? v.chantier_id || null : null, adresse: v.adresse || null }),
    onSuccess: () => { queryClient.invalidateQueries({ queryKey: ['depots'] }); setOpen(false); reset({ type: 'CENTRAL' }); toast.success('Dépôt créé.'); },
    onError: (e) => toast.error(getApiErrorMessage(e)),
  });

  return (
    <div className="space-y-4">
      <div className="flex justify-end"><Button onClick={() => setOpen(true)}><Plus /> Nouveau dépôt</Button></div>
      {isLoading ? <DataTable columns={[]} data={[]} loading /> : (
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
          {(data ?? []).map((d) => (
            <div key={d.id} className="rounded-lg border border-border bg-surface-secondary p-4 shadow-sm">
              <div className="flex items-center justify-between"><p className="font-semibold text-content-primary">{d.libelle}</p><span className="rounded bg-brand-primary/10 px-2 py-0.5 text-xs font-medium text-brand-primary">{d.type === 'CENTRAL' ? 'Central' : 'Chantier'}</span></div>
              <p className="text-xs text-content-muted">{d.code}{d.adresse ? ` · ${d.adresse}` : ''}</p>
            </div>
          ))}
          {(data ?? []).length === 0 && <p className="text-sm text-content-muted">Aucun dépôt. Créez le magasin central.</p>}
        </div>
      )}

      <Modal open={open} onOpenChange={setOpen} title="Nouveau dépôt" footer={<><Button variant="secondary" onClick={() => setOpen(false)}>Annuler</Button><Button onClick={handleSubmit((v) => createMutation.mutate(v))} loading={createMutation.isPending}>Créer</Button></>}>
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <FormField label="Libellé" required className="sm:col-span-2"><Input {...register('libelle')} /></FormField>
          <FormField label="Type"><select {...register('type')} className={selectCls}><option value="CENTRAL">Central (magasin)</option><option value="CHANTIER">Dépôt de chantier</option></select></FormField>
          {type === 'CHANTIER' && (
            <FormField label="Chantier"><select {...register('chantier_id')} className={selectCls}><option value="">— Sélectionner —</option>{chantiers?.items.map((c) => <option key={c.id} value={c.id}>{c.code} · {c.nom}</option>)}</select></FormField>
          )}
          <FormField label="Adresse" className="sm:col-span-2"><Input {...register('adresse')} /></FormField>
        </div>
      </Modal>
    </div>
  );
}
